/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2025 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import * as React from "react";
import { SetStateAction, useCallback, useEffect, useState } from "react";

/**
 * A React hook that manages data hydration between server and client rendering.
 * It automatically serializes and deserializes data through inline script tags,
 * preventing hydration mismatches and allowing for seamless state transfer.
 *
 * @param key - Unique identifier for the hydrated data
 * @param initialData - Default data to use if no hydrated data is found
 * @returns A tuple containing:
 *   - The current data value
 *   - A function to update the data
 *   - A boolean indicating whether the data was hydrated from server
 *
 * @example
 * const [userData, setUserData, wasHydrated] = useInlineHydration('user', { name: '' });
 *
 * @remarks
 * - Automatically injects a script tag with serialized data during client-side rendering
 * - Cleans up injected scripts when component unmounts
 */
export const useInlineHydration = <T extends any>(
  key: string,
  initialData: T
): [T, React.Dispatch<SetStateAction<T>>, boolean] => {
  const [{ wasHydrated, data }, setDataInternal] = useState<{
    wasHydrated: boolean;
    data: T;
  }>(() => {
    if (typeof document !== "undefined") {
      const dataElement = document.querySelector(
        `[data-hydration-key="${key}"]`
      );
      if (dataElement?.textContent) {
        try {
          return {
            wasHydrated: true,
            data: JSON.parse(dataElement.textContent),
          };
        } catch (e) {
          console.error(`Failed to parse ${key} data:`, e);
        }
      }
    }
    return {
      wasHydrated: false,
      data: initialData,
    };
  });

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/json";
    script.setAttribute("data-hydration-key", key);
    script.innerHTML = JSON.stringify(data);

    document.body.appendChild(script);

    return (): void => {
      document.body.removeChild(script);
    };
  }, [data, key]);

  const setData = useCallback((data) => {
    setDataInternal((prev) => ({
      ...prev,
      data,
    }));
  }, []);

  return [data, setData, wasHydrated] as const;
};
